import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';

import { FormDrawer } from '@/components/feedback/drawer';
import { useToast } from '@/components/feedback/toast';
import { SelectField, TextAreaField, UploadField } from '@/components/forms';
import { REPLACEMENT_REASONS, replacementReasonSchema } from '@/features/requests';
import { ApiError } from '@/lib/api/errors';
import type { UploadMessages } from '@/lib/upload';
import { UPLOAD_PRESETS } from '@/lib/upload';

import { useReplacementUploader } from '../../hooks';
import { toUnitOptions } from '../format';
import { useCreateOrderReplacement, useReplacementEligibleItems } from '../hooks';
import { requestReplacementSchema } from '../schemas';
import type { RequestReplacementValues } from '../types';

/** AC 112366 verbatim. */
const SUBMITTED_MESSAGE = 'Your replacement request has been submitted successfully.';

/** ADR-0008 keeps the server's own explanation when there is one; AC 112366's fixed string is the
 *  fallback (grilling deviation: AC 1). */
const SUBMIT_ERROR = 'We could not submit your replacement request. Please try again.';

/** AC 112366 verbatim. Opt-in overrides: the shipped catalog wording stays for the device, component
 *  and bundle forms, whose own stories fixed it (grilling Q9). Inline rather than the dark toast
 *  Figma 382:309203 draws — deliberate, and FLAG-6 with the BA. */
const UPLOAD_MESSAGES = {
  invalidType: 'Only JPG, JPEG or PNG files are supported',
  tooLarge: 'File size must not exceed 5MB',
  tooMany: 'You can upload a maximum of 5 images',
  // `satisfies`, not a plain annotation: every UploadMessages key is optional, so passing this as a
  // variable skips excess-property checking — a mistyped key would compile and silently never render.
} satisfies UploadMessages;

/** Six options, ordered by the shared wire enum — which is the order story 112366 declares them in —
 *  and labelled from the shared map, so the value stays typed as a `ReplacementReason` rather than a
 *  bare string. The frontend owns the display string; the stub's title-case `reasonLabel` is
 *  ignored. */
const REASON_OPTIONS = replacementReasonSchema.options.map((value) => ({
  value,
  label: REPLACEMENT_REASONS[value],
}));

export type RequestReplacementDrawerProps = {
  orderId: string;
  /** Required by the `replacement_request` presign entity, alongside `orderId` (BE, 112366). */
  customerId: string;
  open: boolean;
  onClose: () => void;
};

/**
 * Raises one replacement request against one **Unit** of an order, on a Customer's behalf.
 * Reached from the Actions menu directly, or stacked on the per-order listing via its Request
 * Replacement pill — closing returns to whichever is underneath (ADR-0022 pop-back).
 *
 * Submit is gated on `formState.isValid` with `mode: 'onChange'` so the button tracks validity
 * live (AC 112366; the same pairing as `assign-installer-modal`). The evidence block uses the
 * shared uploader's opt-in filled layout, counter and message overrides (ticket 01, ADO 113415).
 */
export function RequestReplacementDrawer({
  orderId,
  customerId,
  open,
  onClose,
}: RequestReplacementDrawerProps) {
  const form = useForm<RequestReplacementValues>({
    resolver: zodResolver(requestReplacementSchema),
    defaultValues: { unit: '', reason: undefined, description: '', images: [] },
    mode: 'onChange',
  });
  const { data: items, isPending } = useReplacementEligibleItems(orderId, open);
  const create = useCreateOrderReplacement(orderId);
  const uploader = useReplacementUploader({ customerId, orderId });
  const toast = useToast();

  // The visual required marker is driven separately from the schema's `superRefine`, so the marker
  // and the validation rule stay independently correct (grilling decision 8). `useWatch` rather
  // than `form.watch` — the subscription hook the rest of the app uses, and the React Compiler
  // cannot memoize `watch()`.
  const isOthers = useWatch({ control: form.control, name: 'reason' }) === 'others';

  const submit = (values: RequestReplacementValues) =>
    create.mutate(values, {
      onSuccess: () => {
        toast.success({ title: SUBMITTED_MESSAGE });
        // Back to `defaultValues` — we call the parent's `onClose`, so `FormDrawer`'s own
        // reset-on-close never runs for this path.
        form.reset();
        onClose();
      },
      onError: (e) => toast.error({ title: e instanceof ApiError ? e.message : SUBMIT_ERROR }),
    });

  return (
    <FormDrawer
      form={form}
      open={open}
      onClose={onClose}
      title="Request Replacement"
      submitText="Submit"
      onSubmit={submit}
      loading={create.isPending}
      primaryDisabled={!form.formState.isValid}
    >
      <SelectField
        name="unit"
        control={form.control}
        label="Select Item"
        placeholder="Select"
        options={toUnitOptions(items ?? [])}
        loading={isPending}
        required
      />
      <SelectField
        name="reason"
        control={form.control}
        label="Reason for Replacement"
        placeholder="Select"
        options={REASON_OPTIONS}
        required
      />
      <TextAreaField
        name="description"
        control={form.control}
        label="Description"
        placeholder="Enter"
        rows={4}
        maxLength={2000}
        showCount
        required={isOthers}
      />
      <UploadField
        key={`replacement-images-${open}`}
        name="images"
        control={form.control}
        label="Images"
        required
        {...UPLOAD_PRESETS.componentImages}
        onUpload={uploader}
        collapseWhenFilled
        showCounter
        addMoreLabel="Upload more images (JPG, JPEG or PNG)"
        messages={UPLOAD_MESSAGES}
      />
    </FormDrawer>
  );
}
